import { Component, OnInit, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Auth, deleteUser, signOut } from '@angular/fire/auth';
import { AuthService } from '../../core/services/auth.service';
import { AuthApiService } from '../../core/services/auth-api.service';
import { TeamService } from '../../core/services/team.service';
import { OrganizationContextService } from '../../core/services/organization-context.service';
import { FormInputComponent } from '../../shared/components/form-input/form-input.component';
import { FormPasswordComponent } from '../../shared/components/form-password/form-password.component';
import { FormButtonComponent } from '../../shared/components/form-button/form-button.component';
import { LEGAL_URLS } from '../../shared/constants/legal-urls';
import { AuthThemeDirective } from 'src/app/core/directives/auth-theme-directive';
import { BRAND_ASSETS } from 'src/app/core/constants/brand-assets';

/**
 * Signup Page Component
 *
 * View state machine:
 *   role-pick    → first stop when no invitationId. Pilot vs Farmer tiles.
 *   farmer-block → Farmers can only sign up in the CropSwipe mobile app.
 *                   Show download CTAs and a back link.
 *   form         → Pilot path: expanded form mirroring the mobile signup
 *                   (firstName, lastName, email, phone, password, confirm,
 *                    business name, street, city, state, zip, ToS).
 *                   Also used by the invitation path (simpler set of fields,
 *                   no role + no org).
 *   welcome      → Post-signup success + email verification screen.
 *
 * Invitation links (`?invitationId=...`) jump straight to `form` with a
 * reduced field set and use the existing syncProfile path so team-invite
 * signups continue to work as before.
 */
@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    FormInputComponent,
    FormPasswordComponent,
    FormButtonComponent,
    AuthThemeDirective,
  ],
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss']
})
export class SignupComponent implements OnInit {
  readonly logo = BRAND_ASSETS.logoShieldOnDark;

  currentView: 'role-pick' | 'farmer-block' | 'form' | 'welcome' = 'role-pick';

  loading: boolean = false;
  errorMessage: string = '';
  invitationMessage: string = '';

  @ViewChild('password') passwordField?: FormPasswordComponent;
  signupForm!: FormGroup;
  userEmail: string = '';

  invitationId: string | null = null;

  /** Legal-document URLs for the Terms of Service / Privacy Policy links. */
  readonly legalUrls = LEGAL_URLS;

  /**
   * US states + DC for the pilot address dropdown. Mirrors the mobile
   * `CsStateDropdown` (full name shown, 2-letter code stored). The Hub
   * schema expects the 2-letter code, which `runPilotPath` upper-cases.
   */
  readonly states: { code: string; name: string }[] = [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
    { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
  ];

  private auth = inject(Auth);

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private authApiService: AuthApiService,
    private teamService: TeamService,
    private organizationContext: OrganizationContextService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['invitationId']) {
        this.invitationId = params['invitationId'];
        this.invitationMessage = 'You are signing up to accept an organization invitation. Please use the email address the invitation was sent to.';
        // Invited users skip the role gate (the org already exists).
        this.initializeForm('invitation');
        this.currentView = 'form';
      }
    });
  }

  /**
   * Pilot tile click: build the expanded pilot form and switch to the
   * form view. Farmer tile click: switch to the farmer-block view.
   */
  selectPilot(): void {
    this.initializeForm('pilot');
    this.currentView = 'form';
  }

  selectFarmer(): void {
    this.currentView = 'farmer-block';
  }

  /**
   * Back from `farmer-block` or `form` to the role picker.
   */
  backToRolePick(): void {
    this.errorMessage = '';
    this.currentView = 'role-pick';
  }

  /**
   * Initialize the signup form. The shape depends on the path:
   *   - invitation: name + email + password (current behavior).
   *   - pilot: name + email + phone + password + business + address + ToS.
   */
  private initializeForm(mode: 'invitation' | 'pilot'): void {
    if (mode === 'invitation') {
      this.signupForm = this.fb.group({
        firstName: ['', [Validators.required]],
        lastName: ['', [Validators.required]],
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, this.passwordStrengthValidator]],
        confirmPassword: ['', [Validators.required]]
      }, { validators: this.passwordMatchValidator });
      return;
    }

    // Pilot path — full mobile-parity form. Phone is E.164 (`+15551234567`),
    // emitted and validated by the `mask="phone"` input. State is a 2-letter
    // code from the dropdown; zip is 5 digits.
    this.signupForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required]],
      password: ['', [Validators.required, this.passwordStrengthValidator]],
      confirmPassword: ['', [Validators.required]],
      organizationName: ['', [Validators.required]],
      address: ['', [Validators.required]],
      city: ['', [Validators.required]],
      state: ['', [Validators.required]],
      zip: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      agreedToTerms: [false, [Validators.requiredTrue]],
    }, { validators: this.passwordMatchValidator });
  }


  private passwordStrengthValidator: ValidatorFn = (): ValidationErrors | null => {
    return this.passwordField?.allRequirementsMet ? null : { invalidPassword: true }
  }


  private passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');
    return password && confirmPassword && password.value === confirmPassword.value ? null : { passwordMismatch: true }
  }

  /**
   * Submit the signup form. Three branches:
   *
   *   1. Invitation path → existing syncProfile + acceptInvitation flow.
   *   2. Pilot path → Firebase create + token wait + Hub
   *      `/auth/register-with-organization` (atomic profile + org + membership).
   *
   * Both end on the welcome view after Firebase displayName + ADN profile
   * name update.
   */
  async onSignup(): Promise<void> {
    if (this.signupForm.invalid) {
      Object.keys(this.signupForm.controls).forEach(key => {
        this.signupForm.get(key)?.updateValueAndValidity();
        this.signupForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const { firstName, lastName, email, password, phone } = this.signupForm.value;

    const preExistingUid = this.auth.currentUser?.uid ?? null;
    let reachedHub = false;

    try {
      // Stages 1-3 are fatal: without them there is no usable account, so
      // surface the failure and keep the user on the form to retry.
      try {
        // 1. Firebase Auth user (same for both invitation + pilot paths).
        console.log('[Signup] Creating Firebase user...');
        await this.authService.signUp(email, password);

        // 2. Wait for the Firebase backend to issue a usable ID token. The
        //    Hub's TokenResolver middleware needs the same token to verify.
        const token = await this.waitForFirebaseToken();
        if (!token) {
          throw new Error('Failed to get authentication token. Please try logging in.');
        }

        // 3. ADN-side registration.
        reachedHub = true;
        if (this.invitationId) {
          await this.runInvitationPath(firstName, lastName);
        } else {
          await this.runPilotPath();
        }
      } catch (error: any) {
        console.error('[Signup] Registration failed:', error);
        this.errorMessage = this.toErrorMessage(error, 'Registration failed. Please try again.');
        await this.discardPartialFirebaseUser(preExistingUid, !reachedHub);
        return;
      }

      // 4. Firebase displayName mirrors what mobile sets after register.
      //    Cosmetic only — the ADN profile already carries the name from
      //    stage 3 — so a failure here must neither block the welcome view
      //    nor overwrite the (successful) registration with a misleading
      //    "Registration failed" message.
      try {
        await this.authService.updateUserProfile(`${firstName} ${lastName}`.trim());
      } catch (error: any) {
        console.error('[Signup] displayName update failed (non-fatal):', error);
      }

      this.userEmail = email;

      // Straight to the code-entry page — that's where the six digits in the
      // verification email are meant to be typed.
      //
      // The invitation path can finish with a non-fatal warning (wrong-address
      // invite, accept failed) that only the `welcome` view renders, so it
      // keeps the user here instead. Their account exists either way.
      if (this.errorMessage) {
        this.currentView = 'welcome';
      } else {
        // Phone verification only follows when a phone was given (not on the invitation path).
        const state = phone ? { email, phone, next: '/verify/phone' } : { email };
        this.router.navigate(['/verify/email'], { state });
      }
    } catch (error: any) {
      console.error('[Signup] Signup failed:', error);
      this.errorMessage = error.error?.error || error.message || 'Registration failed. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Normalize the error shapes this flow produces into display copy:
   * a Hub `HttpErrorResponse` (message nested under `error.error`), a
   * Firebase/`Error` instance (`message`), or anything else (fallback).
   */
  private toErrorMessage(error: any, fallback: string): string {
    return error?.error?.error || error?.message || fallback;
  }

  /**
   * Deletes possibly orphaned firebase user 
   */
  private async discardPartialFirebaseUser(
    preExistingUid: string | null,
    allowDelete: boolean,
  ): Promise<void> {
    const user = this.auth.currentUser;
    if (!user || user.uid === preExistingUid) {
      return;
    }

    if (allowDelete) {
      try {
        // deleteUser also clears the local session, so no sign-out needed.
        await deleteUser(user);
        this.organizationContext.clearCurrentOrganization();
        return;
      } catch (error) {
        console.error('[Signup] Could not delete orphaned Firebase user:', error);
      }
    }

    try {
      await signOut(this.auth);
    } catch (error) {
      console.error('[Signup] Could not sign out after failed signup:', error);
    }
    
    this.organizationContext.clearCurrentOrganization();
  }

  /**
   * Poll up to 10 seconds for a Firebase ID token. Firebase usually has
   * one ready within a few hundred ms of `createUserWithEmailAndPassword`,
   * but on flaky networks the backend can lag behind the SDK.
   */
  private async waitForFirebaseToken(): Promise<string | null> {
    let token: string | null = null;
    let retries = 0;
    while (!token && retries < 20) {
      try {
        const currentUser = this.auth.currentUser;
        if (currentUser) {
          token = await currentUser.getIdToken(true);
        }
      } catch (_) {
        // Swallow — we retry on a fixed cadence below.
      }
      if (!token) {
        await new Promise(resolve => setTimeout(resolve, 500));
        retries++;
      }
    }
    return token;
  }

  /**
   * Invitation path. Same as the previous signup component: create a bare
   * ADN profile via `/auth/sync-profile`, update the name, then accept
   * the invitation. The invitation grants org membership; no new org is
   * created here.
   */
  private async runInvitationPath(firstName: string, lastName: string): Promise<void> {
    // Retry sync up to 5 times — Firebase backend sometimes lags
    // recognizing the token issued client-side.
    let syncSuccess = false;
    let syncRetries = 0;
    while (!syncSuccess && syncRetries < 5) {
      try {
        const result = await this.authApiService.syncProfile();
        if (result && !result.error) {
          syncSuccess = true;
        } else {
          await new Promise(resolve => setTimeout(resolve, 1000));
          syncRetries++;
        }
      } catch (syncError: any) {
        if (syncError.status === 401 && syncRetries < 4) {
          await new Promise(resolve => setTimeout(resolve, 1500));
          syncRetries++;
        } else {
          throw new Error('Failed to create your profile. Please try logging in.');
        }
      }
    }
    if (!syncSuccess) {
      throw new Error('Failed to create your profile after multiple attempts. Please try logging in.');
    }

    try {
      await this.authApiService.updateProfile({ firstName, lastName });
    } catch (_) {
      // Non-critical — user can update from profile screen later.
    }

    try {
      await this.teamService.acceptInvitation(this.invitationId!);
    } catch (invitationError: any) {
      if (invitationError.message?.includes('different email')) {
        this.errorMessage = 'Account created successfully, but the invitation was sent to a different email address. Please contact the organization administrator.';
      } else {
        this.errorMessage = 'Account created successfully, but there was an issue accepting the invitation. Please contact the organization administrator.';
      }
    }
  }

  /**
   * Pilot path. Calls the Hub's atomic register-with-organization endpoint
   * (same one mobile uses). On success the Hub has created the profile,
   * the organization, and the membership in a single transaction and has
   * set Firebase custom claims so the next token refresh contains the
   * new membership.
   */
  private async runPilotPath(): Promise<void> {
    const v = this.signupForm.value;


    const response = await this.authApiService.registerWithOrganization({
      role: 'pilot',
      firstName: (v.firstName ?? '').trim(),
      lastName: (v.lastName ?? '').trim(),
      phone: v.phone ?? '',
      organizationName: (v.organizationName ?? '').trim(),
      address: (v.address ?? '').trim() || undefined,
      city: (v.city ?? '').trim() || undefined,
      state: (v.state ?? '').trim().toUpperCase() || undefined,
      zip: (v.zip ?? '').trim() || undefined,
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to register pilot');
    }

    // The Hub set custom claims server-side. Refresh the client token so
    // membership shows up immediately in the app shell.
    try {
      await this.authService.refreshSession();
    } catch (_) {
      // refreshSession swallows org-load misses; nothing else to do.
    }
  }

  /**
   * Only reachable from the `welcome` fallback view. The dashboard is gated on
   * a verified address, so this sends them to finish rather than bouncing off
   * AuthGuard on arrival.
   */
  continueToVerification(): void {
    this.router.navigate(['/verify/email'], { state: { email: this.userEmail } });
  }

  async resendVerificationEmail(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';
    try {
      await this.authService.resendEmailVerification();
    } catch (error: any) {
      this.errorMessage = error.message || 'Failed to resend verification email';
    } finally {
      this.loading = false;
    }
  }

  getErrorMessage(controlName: string): string {
    const control = this.signupForm.get(controlName);
    if (!control || !control.touched) {
      return '';
    }

    // `passwordMismatch` is a cross-field error, so it lives on the group
    // rather than on `confirmPassword` itself — that control can be
    // perfectly valid while the pair still disagrees.
    const mismatch = controlName === 'confirmPassword' && this.signupForm.hasError('passwordMismatch');

    if (!control.invalid && !mismatch) {
      return '';
    }

    if (control.hasError('required')) {
      return `${this.formatFieldName(controlName)} is required`;
    }
    if (control.hasError('email')) {
      return 'Please enter a valid email address';
    }
    if (control.hasError('minlength')) {
      const minLength = control.getError('minlength').requiredLength;
      return `Password must be at least ${minLength} characters`;
    }
    if (control.hasError('phone')) {
      return 'Please enter a valid phone number';
    }
    if (control.hasError('pattern')) {
      if (controlName === 'zip') return 'ZIP must be 5 digits';
      return 'Invalid value';
    }
    if (mismatch) {
      return 'Passwords do not match';
    }
    if (control.hasError('invalidPassword')) {
      return 'Password does not meet all requirements';
    }

    return 'Invalid value';
  }

  hasError(controlName: string): boolean {
    return this.getErrorMessage(controlName) !== '';
  }

  private formatFieldName(fieldName: string): string {
    if (fieldName === 'confirmPassword') return 'Confirm Password';
    if (fieldName === 'organizationName') return 'Business Name';
    if (fieldName === 'zip') return 'ZIP Code';
    if (fieldName === 'agreedToTerms') return 'Terms agreement';

    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }
}
